import { useRef } from 'preact/hooks';
import type { Contribution, ReactionSummary } from '../api/types';
import { ContributionCard } from './ContributionCard';
import styles from './ReplyDrawer.module.css';

interface ReplyDrawerProps {
  parent: Contribution;
  replies: Contribution[];
  onClose: () => void;
  onBuildOn?: (contribution: Contribution) => void;
  /**
   * Reaction + parent-jump wiring, forwarded to the reply cards.
   *
   * Without these the cards fell back to their read-only shape: no reaction
   * rail at all, and the "In reply to…" chip rendered disabled. Riffs are
   * contributions like any other and react like any other.
   */
  threadId?: string;
  reactionsByContribution?: ReactionSummary;
  onReact?: (contributionId: string, reactionType: string) => void;
  onJumpToParent?: (parentId: string) => void;
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

const AUTHOR_COLORS = [
  'var(--st-author-1)', 'var(--st-author-2)', 'var(--st-author-3)',
  'var(--st-author-4)', 'var(--st-author-5)', 'var(--st-author-6)',
  'var(--st-author-7)', 'var(--st-author-8)', 'var(--st-author-9)',
  'var(--st-author-10)',
];

function authorColor(id: string | null | undefined): string {
  if (!id) return AUTHOR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AUTHOR_COLORS[Math.abs(hash) % AUTHOR_COLORS.length];
}

export function ReplyDrawer({
  parent,
  replies,
  onClose,
  onBuildOn,
  threadId,
  reactionsByContribution,
  onReact,
  onJumpToParent,
}: ReplyDrawerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleOverlayClick = (event: MouseEvent) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  const parentName = parent.author?.displayName ?? 'Anonymous';
  const parentPreview = parent.content
    ? parent.content.length > 120
      ? `${parent.content.slice(0, 120)}...`
      : parent.content
    : parent.contributionType === 'photo'
      ? '[Photo]'
      : parent.contributionType === 'audio'
        ? '[Audio]'
        : parent.contributionType === 'video'
          ? '[Video]'
          : '';

  return (
    <div
      ref={overlayRef}
      class={styles.overlay}
      onClick={handleOverlayClick}
    >
      <div class={styles.drawer} role="dialog" aria-modal="true" aria-label="Replies">
        <div class={styles.handle} />

        <div class={styles.drawerHeader}>
          <h3 class={styles.drawerTitle}>
            {replies.length} {replies.length === 1 ? 'riff' : 'riffs'}
          </h3>
          <button type="button" class={styles.closeButton} onClick={onClose} aria-label="Close replies">&times;</button>
        </div>

        <div class={styles.parentQuote}>
          <div class={styles.parentQuoteAvatar} style={{ background: authorColor(parent.authorId) }}>
            {getInitials(parentName)}
          </div>
          <div class={styles.parentQuoteContent}>
            <span class={styles.parentQuoteName}>{parentName}</span>
            {parentPreview && <p class={styles.parentQuoteText}>{parentPreview}</p>}
          </div>
        </div>

        <div class={styles.replyList}>
          {replies.map((reply) => (
            <ContributionCard
              key={reply.id}
              contribution={reply}
              authorName={reply.author?.displayName}
              onBuildOn={onBuildOn}
              threadId={threadId}
              reactions={reactionsByContribution?.[reply.id]}
              onReact={onReact}
              onJumpToParent={onJumpToParent}
            />
          ))}
          {replies.length === 0 && (
            <p class={styles.emptyState}>No riffs yet. Be the first to build on this!</p>
          )}
        </div>
      </div>
    </div>
  );
}
